import type {
  GameSnapshot,
  OnboardingStatus,
} from "../types";
import { activeHeartFrontForSnapshot } from "./heart-front-player-context";
import {
  parseHeartChroniclePublicProjection,
  type HeartChronicleOutcome,
  type HeartChroniclePhase,
  type HeartChroniclePublicProjection,
} from "./heart-chronicle-public-projection";
import { presentHeartChronicle } from "./heart-chronicle-presenter";
import {
  parseHeartFrontPublicProjection,
  type HeartFrontPublicId,
  type HeartFrontPublicProjection,
  type HeartFrontPublicStatus,
} from "./heart-front-public-projection";

type UnknownRecord = Record<string, unknown>;

const TERMINAL_ONBOARDING_STATUSES: readonly OnboardingStatus[] = [
  "COMPLETED",
  "LEGACY_COMPLETED",
];

const FRONT_LABELS: Record<HeartFrontPublicId, string> = {
  "heart-front-1": "Front 1",
  "heart-front-2": "Front 2",
  "heart-front-3": "Front 3",
  "heart-front-4": "Front 4",
  "heart-front-5": "Front 5",
  "heart-front-6": "Front 6",
};

const FRONT_STATUS_LABELS: Record<HeartFrontPublicStatus, string> = {
  "not-connected": "non connecté",
  unavailable: "indisponible",
  sealed: "scellé",
  exposed: "exposé",
  "assault-ready": "prêt pour l’assaut",
  breached: "percé",
};

function record(value: unknown): UnknownRecord | undefined {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  try {
    return Object.getPrototypeOf(value) === Object.prototype
      ? value as UnknownRecord
      : undefined;
  } catch {
    return undefined;
  }
}

function nonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function terminalOnboardingStatus(value: unknown): value is OnboardingStatus {
  return typeof value === "string" &&
    TERMINAL_ONBOARDING_STATUSES.includes(value as OnboardingStatus);
}

function optionalTerminalFieldTypesAreValid(
  onboarding: UnknownRecord,
  status: OnboardingStatus,
): boolean {
  for (const field of ["startedAt", "completedAt", "protectionEndedAt"]) {
    if (
      Object.prototype.hasOwnProperty.call(onboarding, field) &&
      onboarding[field] !== null &&
      typeof onboarding[field] !== "string"
    ) {
      return false;
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(onboarding, "protectionActive") &&
    onboarding.protectionActive !== false
  ) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(onboarding, "progressCount") &&
    !nonNegativeSafeInteger(onboarding.progressCount)
  ) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(onboarding, "totalRequiredMilestones") &&
    !nonNegativeSafeInteger(onboarding.totalRequiredMilestones)
  ) {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(onboarding, "protectionEndReason")) {
    const reason = onboarding.protectionEndReason;
    const expectedReason = status === "COMPLETED"
      ? "ONBOARDING_COMPLETED"
      : "LEGACY_PROFILE_MIGRATION";
    if (reason !== expectedReason) {
      return false;
    }
  }

  return true;
}

export interface ReturnBriefingEligibility {
  readonly stateVersion: number;
  readonly onboardingRevision: number;
  readonly bastionLevel: number;
}

/**
 * Reads only the runtime fields that authorize this briefing. Missing or
 * contradictory authority data produces no eligibility and no fallback.
 */
export function parseReturnBriefingEligibility(
  snapshot: unknown,
): ReturnBriefingEligibility | undefined {
  try {
    const envelope = record(snapshot);
    const state = record(envelope?.state);
    const buildings = record(state?.buildings);
    const onboarding = record(state?.onboarding);
    if (
      !envelope ||
      envelope.initialized !== true ||
      !state ||
      !buildings ||
      !onboarding ||
      !nonNegativeSafeInteger(state.stateVersion) ||
      !nonNegativeSafeInteger(buildings.bastion) ||
      onboarding.schemaVersion !== 1 ||
      !terminalOnboardingStatus(onboarding.status) ||
      onboarding.currentMilestoneId !== null ||
      !nonNegativeSafeInteger(onboarding.revision) ||
      !optionalTerminalFieldTypesAreValid(onboarding, onboarding.status)
    ) {
      return undefined;
    }

    return Object.freeze({
      stateVersion: state.stateVersion,
      onboardingRevision: onboarding.revision,
      bastionLevel: buildings.bastion,
    });
  } catch {
    return undefined;
  }
}

export function isReturnBriefingEligible(
  snapshot: unknown,
): snapshot is Extract<GameSnapshot, { initialized: true }> {
  return parseReturnBriefingEligibility(snapshot) !== undefined;
}

export interface ReturnBriefingBastionSignal {
  readonly kind: "BASTION";
  readonly revision: number;
  readonly level: number;
  readonly text: string;
}

export interface ReturnBriefingFrontSignal {
  readonly kind: "FRONT";
  readonly frontId: HeartFrontPublicId;
  readonly status: HeartFrontPublicStatus;
  readonly revision: number;
  readonly text: string;
}

export interface ReturnBriefingChronicleSignal {
  readonly kind: "CHRONIQUE";
  readonly chroniclePublicId: string;
  readonly phase: HeartChroniclePhase;
  readonly heldFrontCount: HeartChroniclePublicProjection["heldFrontCount"];
  readonly lastOutcome: HeartChronicleOutcome;
  readonly revision: number;
  readonly text: string;
}

export type ReturnBriefingSignal =
  | ReturnBriefingBastionSignal
  | ReturnBriefingFrontSignal
  | ReturnBriefingChronicleSignal;

export interface ReturnBriefingApi {
  heartFronts(): Promise<unknown>;
  heartChronicle(): Promise<unknown>;
}

export interface ReturnBriefingReadBatch {
  readonly bastion: ReturnBriefingBastionSignal;
  readonly front: Promise<ReturnBriefingFrontSignal | undefined>;
  readonly chronicle: Promise<ReturnBriefingChronicleSignal | undefined>;
}

export interface ReturnBriefingReadSession {
  start(): ReturnBriefingReadBatch | undefined;
}

/** Caches the first voluntary read batch for the lifetime of one component. */
export function createReturnBriefingReadSession(
  api: ReturnBriefingApi,
  snapshot: unknown,
): ReturnBriefingReadSession {
  let batch: ReturnBriefingReadBatch | undefined;
  return {
    start() {
      if (batch) return batch;
      const bastion = createReturnBriefingBastionSignal(snapshot);
      if (!bastion) return undefined;
      batch = Object.freeze({
        bastion,
        front: Promise.resolve().then(() => api.heartFronts()).then((projection) =>
          createReturnBriefingFrontSignal(snapshot, projection)
        ),
        chronicle: Promise.resolve().then(() => api.heartChronicle()).then((projection) =>
          createReturnBriefingChronicleSignal(projection)
        ),
      });
      return batch;
    },
  };
}

export function createReturnBriefingBastionSignal(
  snapshot: unknown,
): ReturnBriefingBastionSignal | undefined {
  const eligibility = parseReturnBriefingEligibility(snapshot);
  if (!eligibility) return undefined;
  return Object.freeze({
    kind: "BASTION" as const,
    revision: eligibility.stateVersion,
    level: eligibility.bastionLevel,
    text: `Bastion — niveau ${eligibility.bastionLevel}`,
  });
}

export function hasProvenActiveHeartFront(snapshot: unknown): boolean {
  try {
    return activeHeartFrontForSnapshot(snapshot) !== undefined;
  } catch {
    return false;
  }
}

export function createReturnBriefingFrontSignal(
  snapshot: unknown,
  projection: unknown,
): ReturnBriefingFrontSignal | undefined {
  let activeFrontId: ReturnType<typeof activeHeartFrontForSnapshot>;
  let parsed: HeartFrontPublicProjection;
  try {
    activeFrontId = activeHeartFrontForSnapshot(snapshot);
    if (!activeFrontId) return undefined;
    parsed = parseHeartFrontPublicProjection(projection);
  } catch {
    return undefined;
  }

  const row = parsed.fronts.find((candidate) => candidate.frontId === activeFrontId);
  if (!row) return undefined;
  return Object.freeze({
    kind: "FRONT" as const,
    frontId: row.frontId,
    status: row.status,
    revision: parsed.revision,
    text: `${FRONT_LABELS[row.frontId]} — statut : ${FRONT_STATUS_LABELS[row.status]}.`,
  });
}

export function createReturnBriefingChronicleSignal(
  projection: unknown,
): ReturnBriefingChronicleSignal | undefined {
  let parsed: HeartChroniclePublicProjection;
  try {
    parsed = parseHeartChroniclePublicProjection(projection);
  } catch {
    return undefined;
  }

  const presentation = presentHeartChronicle(parsed, null, 0);
  return Object.freeze({
    kind: "CHRONIQUE" as const,
    chroniclePublicId: parsed.chroniclePublicId,
    phase: parsed.phase,
    heldFrontCount: parsed.heldFrontCount,
    lastOutcome: parsed.lastOutcome,
    revision: parsed.revision,
    text: `Chronique — ${presentation.crownStateText} ${presentation.heldFrontsText}. ${presentation.outcomeText}`,
  });
}

export function buildReturnBriefingSignals(
  snapshot: unknown,
  frontProjection?: unknown,
  chronicleProjection?: unknown,
): readonly ReturnBriefingSignal[] {
  const bastion = createReturnBriefingBastionSignal(snapshot);
  const front = frontProjection === undefined
    ? undefined
    : createReturnBriefingFrontSignal(snapshot, frontProjection);
  const chronicle = chronicleProjection === undefined
    ? undefined
    : createReturnBriefingChronicleSignal(chronicleProjection);
  return [bastion, front, chronicle].filter(
    (signal): signal is ReturnBriefingSignal => signal !== undefined,
  );
}
